<template>
  <div
    class="AtlasWidgetDropdown"
    :class="{ 'AtlasWidgetDropdown--detached': detached !== false }"
    v-click-outside="onClickAway"
  >
    <a
      class="AtlasWidgetDropdown-trigger"
      :class="{ 'is-active': isActive }"
      @click="isActive = !isActive"
    >
      <slot />

      <atlas-icon
        class="AtlasWidgetDropdown-arrow"
        name="select-arrow"
        :size="10"
      />
    </a>

    <div class="AtlasWidgetDropdown-content" v-if="$slots.content && isActive">
      <slot name="content" />
    </div>

    <ul
      class="AtlasWidgetDropdown-items"
      v-else-if="optionsIsNotEmpty && isActive"
    >
      <li
        class="AtlasWidgetDropdown-item"
        v-for="(option, i) in options"
        :key="i"
      >
        <div
          class="AtlasWidgetDropdown-link"
          :class="{
            'is-hasIcon': option.icon,
            'is-active': option.isActive,
          }"
          @click="goToOption(option.to)"
          v-if="!option.isDisabled"
        >
          {{ option.text }}
          <span class="AtlasWidgetDropdown-icon" v-if="option.icon">
            <atlas-icon :name="option.icon" :size="20" />
          </span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import uuid from 'uuid/v4';
import { mapMutations, mapState } from 'vuex';

export default {
  name: 'atlas-widget-dropdown',

  props: {
    options: {
      type: Array,
      default: () => [],
    },

    detached: {
      type: Boolean,
      default: false,
    },

    keepOpen: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['choose'],

  data: () => ({
    id: uuid(),
  }),

  computed: {
    ...mapState('ui', ['activeDropdown', 'isMapFiltersActive']),

    isActive: {
      get() {
        // "detached" is assumed to just mean map filters (slider) for now
        if (this.detached && this.isMapFiltersActive) return true;
        return this.activeDropdown === this.id;
      },

      set(isActive) {
        if (this.detached) {
          if (isActive) this.openMapFilters();
          else this.closeMapFilters();
        } else if (isActive) {
          this.openDropdown({ id: this.id });
        } else {
          this.closeDropdown();
        }
      },
    },
    optionsIsNotEmpty() {
      return !!(this.options && this.options.length);
    },
  },

  methods: {
    ...mapMutations('ui', [
      'openDropdown',
      'closeDropdown',
      'openMapFilters',
      'closeMapFilters',
    ]),

    goToOption(option) {
      this.$emit('choose', { option });
    },

    onClickAway() {
      if (this.keepOpen === false && this.isActive) this.isActive = false;
    },
  },
};
</script>

<style lang="scss">
.AtlasWidgetDropdown {
  $item-height: 45px;
  $icon-size: 22px;
  $trigger-pad-y: round(math.div(($item-height - $icon-size), 2));
  position: relative;

  display: inline-block;
  font-size: 16px;
  vertical-align: top;

  &--detached {
    position: static;
  }

  &-arrow {
    margin-left: 12px;
  }

  &-trigger {
    background-color: color(bg);
    margin-left: 1px;
    margin-right: 1px;
    padding: ($trigger-pad-y + 1px) $gutter ($trigger-pad-y - 2px);
    color: color(primary);
    cursor: pointer;
    font-weight: bold;
    line-height: $icon-size;
    text-transform: uppercase;
    display: flex;
    align-items: center;

    &:hover {
      color: color(black);
    }

    &.is-active {
      @include elevation;
      background-color: color(bg);
      color: color(black);
      // Mask
      &::after {
        @include position(absolute, null 0 0 0);
        z-index: 2;
        display: block;
        height: 5px;
        background-color: color(bg);
        content: '';
      }

      // Reset on detached dropdowns (ie. slider)
      .AtlasWidgetDropdown--detached & {
        @include elevation(0);
        background-color: transparent;
        color: color(primary);

        &:hover {
          color: color(black);
        }

        &::after {
          display: none;
        }
      }
    }
  }

  &-content,
  &-items {
    @include elevation;
    position: absolute;
    z-index: 1;
    left: 0;
    min-width: 225px;
    background-color: color(bg);

    .AtlasWidgetDropdown--detached & {
      right: $gutter;
      left: auto;
      width: 226px;
    }
  }

  &-items {
    overflow: auto;
    max-height: 600px;
    padding: 0;
    margin: 0;
    font-size: 15px;
    font-weight: normal;
    // line-height: 2;
    line-height: 1.5;
  }

  &-item {
    position: relative;
    display: block;
    border-bottom: 1px solid color(rule);

    &:last-child {
      border-bottom: 0;
    }
  }

  &-link {
    display: block;
    min-height: 3em;
    padding: 12px $gutter 11px;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      background-color: color(rule);
    }

    &.is-hasIcon {
      padding-right: 44px;
    }

    &.is-active {
      cursor: default;
      font-weight: bold;
      pointer-events: none;
    }
  }

  .AtlasWidgetDropdown-item a {
    // Has to override `.Indicator a`, hence the extra specificity
    color: inherit;
  }

  &-icon {
    @include position(absolute, 50% 12px null null);
    transform: translateY(-50%);
  }
}
</style>
